import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { animated, useSpring } from '@react-spring/web';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled, { css } from 'styled-components';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Glitch } from '@sorare/core/src/atoms/animations/Glitch';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconCheck } from '@sorare/core/src/atoms/icons/system/iconCheck.ts';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { BrandXS, BrandXXS } from '@sorare/core/src/atoms/typography';
import { getRarityColor } from '@sorare/core/src/lib/colors';
import {
  getPositionInitials,
  getPositionName,
} from '@sorare/core/src/lib/positions';
import type { Font } from '@sorare/core/src/lib/text';
import { getTextSize } from '@sorare/core/src/lib/text';

import competitionClue from '#src/assets/card-pull-experience/competition.svg';
import divisionClue from '#src/assets/card-pull-experience/division.svg';
import holdIcon from '#src/assets/card-pull-experience/hold.svg';
import countryClue from '#src/assets/card-pull-experience/nationality.svg';
import positionClue from '#src/assets/card-pull-experience/position.svg';
import { CardPlaceholder } from '#src/components/essence/CardPullExperience/CardPlaceholder/index.tsx';

import type { RevealingCardPlaceholder_cardPulledCard } from './__generated__/index.graphql.ts';

const HOLD_DURATION_MS = 2000;

const StyledCardPlaceholder = styled(CardPlaceholder)`
  height: 100%;
  width: 100%;
  border-radius: 4.16%;
`;

const Wrapper = styled(ButtonBase)<{
  selected: boolean;
}>`
  position: relative;
  display: block;
  transition: transform 0.2s ease-out;
  touch-action: manipulation;
  user-select: none;
  /* iOS long-press image callout would hijack the hold gesture */
  -webkit-touch-callout: none;

  img,
  image {
    pointer-events: none;
  }

  &:before {
    content: '';
    border-radius: var(--unit);
    position: absolute;
    inset: 0;
    box-shadow: 0px 0px 40px 0 var(--color);
    opacity: 0;
    transition: opacity 0.2s ease-out;
  }
  &:focus-visible {
    outline: none;
  }
  &:hover:not(:disabled),
  &:focus:not(:disabled) {
    transform: scale(1.05);
    &:before {
      opacity: 0.4;
    }
  }
  &.holding {
    transform: scale(1.05);
    &:before {
      opacity: 1;
    }
  }

  .hold-hint {
    opacity: 0;
    transition: opacity 0.15s ease-out;
  }
  &:hover:not(:disabled) .hold-hint,
  &:focus:not(:disabled) .hold-hint {
    opacity: 1;
  }
  ${({ selected }) =>
    selected &&
    css`
      &:before {
        opacity: 1;
      }
    `}
`;

const ProgressFill = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 4.16%;
  overflow: hidden;
  pointer-events: none;
  z-index: 1;
`;

const Fill = styled(animated.div)`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--color);
`;

const CenterOverlay = styled(Vertical).attrs({ center: true, gap: 0.5 })`
  position: absolute;
  inset: 0;
  justify-content: center;
  pointer-events: none;
  color: var(--c-white);
  z-index: 2;
`;

const CenterIcon = styled.span`
  display: inline-block;
  width: var(--triple-unit);
  height: var(--triple-unit);
  background-color: currentColor;
  mask: url(${holdIcon}) center / contain no-repeat;
`;

const RestHint = styled(BrandXXS)`
  position: absolute;
  bottom: 6%;
  left: var(--unit);
  right: var(--unit);
  text-align: center;
  color: var(--c-nd-700);
  pointer-events: none;
  z-index: 2;
`;

const CornerIcon = styled.span`
  position: absolute;
  right: var(--unit);
  bottom: var(--unit);
  width: var(--double-unit);
  height: var(--double-unit);
  background-color: var(--c-nd-600);
  mask: url(${holdIcon}) center / contain no-repeat;
  pointer-events: none;
  z-index: 2;
`;

const AttributesWrapper = styled(Horizontal).attrs({
  gap: 1,
})`
  position: absolute;
  bottom: 15%;
  left: var(--half-unit);
  right: var(--half-unit);
`;

const CraftingClue = styled.img`
  height: var(--double-unit);
  object-fit: contain;
  mask-image: linear-gradient(var(--c-nd-600), var(--c-nd-600));
`;

const StyledGlitch = styled(Glitch)`
  height: 32px;
  max-width: 56px;
  img {
    height: 22px;
    max-width: 56px;
    object-fit: contain;
  }
  &.country {
    border-radius: var(--half-unit);
    overflow: hidden;
  }
`;

const DIVISION_FONT_SIZE = 14;
const DIVISION_MAX_WIDTH = 80;
const DIVISION_MAX_HEIGHT = 40;
const getDivisionFont = (size: number): Font => `normal ${size}px pressio`;

const getDivisionNameStyle = (
  name: string
): { fontSize?: number; lineHeight?: string } => {
  const words = name.split(' ');
  if (words.length <= 1) return {};

  // Each word wraps to its own line within 80px
  const fontSize = Math.min(
    DIVISION_FONT_SIZE,
    Math.floor(DIVISION_MAX_HEIGHT / words.length)
  );
  if (fontSize === DIVISION_FONT_SIZE) return {};

  // Verify the longest word fits at the reduced font size
  const longestWord = words.reduce((a, b) => (a.length > b.length ? a : b));
  const { width } = getTextSize(
    longestWord.toUpperCase(),
    getDivisionFont(fontSize)
  );
  const adjustedSize =
    width > DIVISION_MAX_WIDTH
      ? fontSize * (DIVISION_MAX_WIDTH / width)
      : fontSize;
  return { fontSize: adjustedSize, lineHeight: `${adjustedSize}px` };
};

const useHoldToConfirm = ({
  disabled,
  onConfirm,
  onHoldStart,
  onHoldCancel,
}: {
  disabled?: boolean;
  onConfirm?: () => void;
  onHoldStart?: () => boolean | void;
  onHoldCancel?: () => void;
}) => {
  const [holding, setHolding] = useState(false);
  const [completed, setCompleted] = useState(false);
  const holdingRef = useRef(false);
  const confirmTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const disabledRef = useRef(disabled);
  const onConfirmRef = useRef(onConfirm);
  const onHoldStartRef = useRef(onHoldStart);
  const onHoldCancelRef = useRef(onHoldCancel);
  useEffect(() => {
    disabledRef.current = disabled;
  }, [disabled]);
  useEffect(() => {
    onConfirmRef.current = onConfirm;
  }, [onConfirm]);
  useEffect(() => {
    onHoldStartRef.current = onHoldStart;
  }, [onHoldStart]);
  useEffect(() => {
    onHoldCancelRef.current = onHoldCancel;
  }, [onHoldCancel]);

  const [{ progress }, api] = useSpring(() => ({ progress: 0 }));

  const start = useCallback(() => {
    if (disabledRef.current || !onConfirmRef.current) return;
    if (holdingRef.current || confirmTimeoutRef.current !== null) return;
    if (onHoldStartRef.current?.() === false) return;
    holdingRef.current = true;
    setHolding(true);
    setCompleted(false);
    api.start({
      from: { progress: 0 },
      to: { progress: 1 },
      config: { duration: HOLD_DURATION_MS },
      onResolve: ({ finished }) => {
        if (!finished) return;
        holdingRef.current = false;
        setHolding(false);
        setCompleted(true);
        confirmTimeoutRef.current = setTimeout(() => {
          confirmTimeoutRef.current = null;
          setCompleted(false);
          api.set({ progress: 0 });
          if (!disabledRef.current) onConfirmRef.current?.();
        }, 350);
      },
    });
  }, [api]);

  const cancel = useCallback(() => {
    if (confirmTimeoutRef.current !== null) return;
    if (!holdingRef.current) return;
    holdingRef.current = false;
    setHolding(false);
    api.stop();
    api.set({ progress: 0 });
    onHoldCancelRef.current?.();
  }, [api]);

  useEffect(() => {
    const onInterrupt = () => cancel();
    const onVisibility = () => {
      if (document.hidden) cancel();
    };
    window.addEventListener('blur', onInterrupt);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('blur', onInterrupt);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [cancel]);

  return { holding, completed, progress, start, cancel };
};

type Props = {
  rarity: Rarity;
  selected: boolean | null;
  onConfirm?: () => void;
  onHoldStart?: () => boolean | void;
  onHoldCancel?: () => void;
  disabled: boolean;
  cardPulledCard: RevealingCardPlaceholder_cardPulledCard;
};

export const RevealingCardPlaceholder = ({
  rarity,
  selected,
  onConfirm,
  onHoldStart,
  onHoldCancel,
  disabled,
  cardPulledCard,
}: Props) => {
  const { formatMessage } = useIntl();
  const { holding, completed, progress, start, cancel } = useHoldToConfirm({
    disabled,
    onConfirm,
    onHoldStart,
    onHoldCancel,
  });
  const active = holding || completed;
  const { country, competition, position, division } = cardPulledCard;
  const divisionStyle = useMemo(
    () => (division ? getDivisionNameStyle(division.name) : {}),
    [division]
  );
  const attributeRevealed = [
    position,
    division?.name,
    competition?.slug,
    country?.slug,
  ].filter(value => value !== null && value !== undefined);

  return (
    <Wrapper
      data-revealing-card
      aria-label={formatMessage({ defaultMessage: 'Hold to reveal card' })}
      className={classNames({ holding })}
      onPointerDown={e => {
        if (e.button !== 0) return;
        // Safari does not focus buttons on tap
        e.currentTarget.focus();
        e.currentTarget.setPointerCapture(e.pointerId);
        start();
      }}
      onPointerUp={cancel}
      onPointerCancel={cancel}
      onKeyDown={e => {
        if (e.repeat) return;
        if (e.key !== ' ' && e.key !== 'Enter') return;
        e.preventDefault();
        start();
      }}
      onKeyUp={e => {
        if (e.key !== ' ' && e.key !== 'Enter') return;
        cancel();
      }}
      onContextMenu={e => e.preventDefault()}
      disabled={disabled}
      selected={selected ?? false}
      style={
        {
          '--color': getRarityColor(rarity),
        } as CSSProperties
      }
    >
      <StyledCardPlaceholder rarity={selected === false ? undefined : rarity}>
        {attributeRevealed.length === 0 && !active && (
          <path
            opacity="0.4"
            d="M62.2844 131.742V117.158L75.5727 111.183V96.2229H59.4011V108.675H48.9961V101.99C48.9961 91.9606 54.1359 87.364 67.466 87.364C80.7961 87.364 85.9777 91.9606 85.9777 101.99V115.696L72.6894 121.671V131.742H62.2844ZM61.4069 149V137.926H73.5669V149H61.4069Z"
            fill="white"
            fillOpacity="0.43"
          />
        )}
      </StyledCardPlaceholder>
      {!!active && (
        <>
          <ProgressFill>
            <Fill
              style={{
                height: progress.to(p => `${p * 100}%`),
                opacity: progress.to(p => 0.5 + 0.5 * p),
                maskImage: progress.to(
                  p =>
                    `linear-gradient(to top, var(--c-black) ${p * 100}%, transparent)`
                ),
              }}
            />
          </ProgressFill>
          <CenterOverlay aria-live="polite">
            {completed ? (
              <FontAwesomeIcon icon={iconCheck} width={24} height={24} />
            ) : (
              <CenterIcon />
            )}
            <BrandXXS>
              <animated.span>
                {progress.to(
                  p => `${Math.ceil((1 - p) * (HOLD_DURATION_MS / 1000))}s`
                )}
              </animated.span>
            </BrandXXS>
          </CenterOverlay>
        </>
      )}
      {!disabled && !active ? (
        <>
          <RestHint className="hold-hint">
            <FormattedMessage defaultMessage="Tap and hold to confirm" />
          </RestHint>
          <CornerIcon />
        </>
      ) : null}
      <AttributesWrapper center>
        {!!country && (
          <Tooltip placement="top" title={country.name}>
            <Vertical center>
              <CraftingClue src={countryClue} alt="" />

              <StyledGlitch className="country">
                <img src={country.flagUrl} alt={country.name} />
              </StyledGlitch>
            </Vertical>
          </Tooltip>
        )}
        {!!competition?.pictureUrl && (
          <Tooltip placement="top" title={competition.displayName}>
            <Vertical>
              <CraftingClue src={competitionClue} alt="" />
              <StyledGlitch>
                <img
                  src={competition.pictureUrl}
                  alt={competition.displayName}
                />
              </StyledGlitch>
            </Vertical>
          </Tooltip>
        )}
        {!!position && (
          <Tooltip placement="top" title={getPositionName(position)}>
            <Vertical center>
              <CraftingClue src={positionClue} alt="" />
              <StyledGlitch>
                <BrandXS>{getPositionInitials(position)}</BrandXS>
              </StyledGlitch>
            </Vertical>
          </Tooltip>
        )}
        {!!division && (
          <Tooltip placement="top" title={division.name}>
            <Vertical center>
              <CraftingClue src={divisionClue} alt="" />
              <StyledGlitch>
                <BrandXS style={divisionStyle}>{division.name}</BrandXS>
              </StyledGlitch>
            </Vertical>
          </Tooltip>
        )}
      </AttributesWrapper>
    </Wrapper>
  );
};

RevealingCardPlaceholder.fragments = {
  cardPulledCard: gql`
    fragment RevealingCardPlaceholder_cardPulledCard on CardPullCard {
      id
      country {
        slug
        name
        flagUrl
      }
      competition {
        slug
        displayName
        pictureUrl
      }
      position
      division {
        name
      }
    }
  ` as TypedDocumentNode<RevealingCardPlaceholder_cardPulledCard>,
};
